<template>
    <div class="WebApp">
        <notification-bar :text="notificationForAll" v-if="!!notificationForAll" v-on:notification-click="onNotificationClicked"></notification-bar>
        <div class='bar-container'>
            <headerbar :user="user" :service="service"></headerbar>
            <notification-bar :text="notificationText" v-if="!!notificationText"></notification-bar>
            <sessionapp v-on:currentSlide="getCurrentSlide($event)" :surveys="surveys" :progressiveReveal="progressiveReveal" :user="user" :service="service" :currentSlide="currentSlide" :presentation="activePres" :slideList="sortedSlides" :syncSlideNum="syncSlideNum"></sessionapp>
            <bottombar :currentSlide="currentSlide" :chatContacts="chatContacts" :chatList="chatList" :slideList="sortedSlides" :presentation="activePres" :presentationList="presentationList" :peopleList="peopleList" :questionList="questionList" :peopleMap="peopleMap" :user="user" :isLoading="isLoading" v-on:getPresentations="getPresentations" :isAllowedToListSessions="isAllowedToListSessions"></bottombar>
        </div>
        <leadgen :service="service" :activePres="activePres" :user="user"></leadgen>
    </div>
</template>

<script>

import HeaderBar from '@/components/HeaderBar';
import BottomBar from '@/components/BottomBar';
import SessionApp from '@/components/SessionApp';
import LeadGen from '@/components/LeadGen';
import {EventBus} from '../router/index';
import NotificationBar from '@/components/NotificationBar';
import NavigatorUtils from '../javascript-classes/utils/navigator';

const androidIntent = 'intent://join/#Intent;package=com.dialogloop.dialogloop_android;action=android.intent.action.VIEW;S.browser_fallback_url=https%3A%2F%2Fplay.google.com%2Fstore;end';
const iOSIntent = 'dialogloop://';

function getParam(url, name, defaultValue) {
    if (!url || !name) {
        return;
    }

    const regexp = new RegExp(`[?&#]${name}=`);
    var start = url.search(regexp);

    if (start < 0) {
        return defaultValue;
    }

    start += name.length + 2;

    const end = url.substr(start).search(/[?&#]/);

    return url.substr(start, end >= 0 ? end : undefined);
}

export default {
    components: {
        headerbar: HeaderBar,
        bottombar: BottomBar,
        sessionapp: SessionApp,
        leadgen: LeadGen,
        'notification-bar': NotificationBar
    },

    props: {
        service: Object
    },

    data () {
        return {
            isReady: false,
            isLoading: true,
            activePres: undefined,
            presentationList: {},
            questionList: undefined,
            peopleList: undefined,
            slideList: [], // Unsorted chain list
            user: {},
            syncSlideNum: 0,
            peopleMap: {},
            currentSlide: 0,
            progressiveReveal: 0,
            chatList: undefined,
            chatContacts: undefined,
            revealOn: false,
            surveys: undefined,
            isAllowedToListSessions: false,
            notificationText: undefined,
            notificationForAll: undefined,
            connectionParams: undefined
        };
    },

    computed: {
        sortedSlides() {
            if (!this.slideList || this.slideList.length === 0) {
                return [];
            }

            let tmp = JSON.parse(JSON.stringify(this.slideList));
            let sortedList = [];

            // Find first slide
            for(let i = 0; i < tmp.length; ++i) {
                if (tmp[i].prev === "0") {
                    sortedList.push(tmp.splice(i, 1)[0]);
                    break;
                }
            }

            while(tmp.length > 0) {
                let found = false;

                for(let i = 0; i < tmp.length; ++i) {
                    if (sortedList[sortedList.length - 1].next === tmp[i].id) {
                        sortedList.push(tmp.splice(i, 1)[0]);
                        found = true;
                        break;
                    }
                }

                if (!found) {
                    console.error(`Error processing sorted slides list. Could not find slide with Id: ${sortedList[sortedList.length - 1].next}`);
                    break;
                }
            }

            return sortedList;
        }
    },

    watch: {
        $route (to, from){
            this.init();
        }
    },

    async created() {
        this.service.join({
            url: window.location.href
        }).then((user) => {
            this.user = user;
            this.init();

            // Show notification if we can use native application
            if (getParam(window.location.href, 'beta-enable-native', false)) {
                this.service.getConnection(window.location.href).then((params) => {
                    if (!params) {
                        return;
                    }

                    this.connectionParams = params;

                    if (NavigatorUtils.isAndroid()) {
                        this.notificationForAll = `{$this.$t('str.UseAndroidNativeApp')}}`;
                    } else if (NavigatorUtils.isIOS()) {
                        this.notificationForAll = `{${this.$t('str.UseIOSNativeApp')}}`;
                    }
                });
            }
        }).catch((err) => {
            if (err.code === 401) {
                // Show login page with social network selection
                this.$router.push({
                    path: 'login',
                    query: this.$route.query
                });
            } else {
                console.error(err);
                this.notificationText = this.$t("str.SorryUnableToLogin");
            }
        });
    },

    methods: {
        init() {
            this.notificationText = undefined;
            this.notificationForAll = undefined;
            this.isLoading = true;
            this.isAllowedToListSessions = false;

            this.service.getSession({
                url: window.location.href
            }).then(async (handle) => {
                this.isReady = true;

                if (handle.session) {
                    this.isAllowedToListSessions = true;
                    this.presentationList = await this.getPresentations(handle.session);
                } else if (handle.presentation) {
                    let presentation = await this.service.getPresentation(handle.presentation);
                    this.openPresentation(presentation);
                } else {
                    console.error("Invalid handle");
                    this.notificationText = this.$t("str.UnexpectedError");
                    return;
                }

                this.resetSurveys();
                this.isLoading = false;
            }).catch((err) => {
                console.error(err);
                this.notificationText = this.$t("str.SorryUnableToGetSessionInfo");
                this.isLoading = false;
            });
        },

        getCurrentSlide(newSlide) {
            this.currentSlide = newSlide;
        },

        addPresentationListeners() {
            this.service.addEventListener('slideChanged', (slide) => {
                if (this.revealOn === false) {
                    this.revealOn = true;
                    this.progressiveReveal = this.syncSlideNum;
                }

                this.syncSlideNum = this.getSlideIndexFromId(slide.id);
                
                if (this.syncSlideNum > this.progressiveReveal) {
                    this.progressiveReveal = this.syncSlideNum;
                }
            });
            
            this.service.addEventListener('attendeeJoined', (attendee) => {
                if (!this.peopleList) {
                    return;
                }

                for (var i = 0; i < this.peopleList.length; i++) {
                    if (this.peopleList[i].id === attendee.id) {
                        return;
                    }
                }

                this.peopleList.push(attendee);
                this.peopleMap.set(attendee.id, attendee);  
            });

            this.service.addEventListener('questionAdded', (question) => {
                if (this.questionList === undefined) {
                    return;
                }

                this.questionList.push(question);
                this.questionList.sort((a, b) => {
                    return a.timestamp - b.timestamp;
                });
                EventBus.$emit('questionsChanged');
            });

            this.service.addEventListener('questionLiked', (question) => {
                if (this.questionList === undefined) {
                    return;
                }

                this.questionList.splice(this.questionList.findIndex((oldQuestion) => {
                    return oldQuestion.id === question.id;
                }), 1);
                this.questionList.push(question);
                this.questionList.sort((a, b) => { // TODO: Insert in the right location right away to avoid resorting the whole array
                    return a.timestamp - b.timestamp;
                });
            });

            this.service.addEventListener('questionPropsUpdated', (question) => {
                if (this.questionList === undefined) {
                    return;
                }

                let oldIndex = this.questionList.findIndex((oldQuestion) => {
                    return oldQuestion.id === question.id;
                });

                this.questionList[oldIndex].props = question.props;
                this.questionList[oldIndex].replies = question.replies;
            });

            this.service.addEventListener('slideUploaded', async (slide) => {
                this.slideList = await this.service.getSlides(this.activePres.id);
                this.syncSlideNum = this.getSlideIndexFromId(this.activePres.currentSlide);
            });

            this.service.addEventListener('slideDeleted', async (data) => {
                this.slideList = await this.service.getSlides(this.activePres.id);
                this.syncSlideNum =  this.getSlideIndexFromId(this.activePres.currentSlide);
            });

            this.service.addEventListener('surveyStarted', (survey) => {
                if (!this.activePres || !this.activePres.props) {
                    return;
                }

                if (!this.activePres.props.SurveyInstances) {
                    this.$set(this.activePres.props, 'SurveyInstances', []);
                }

                let instanceId = this.activePres.props.SurveyInstances.find((instanceId) => {
                    return instanceId === survey.instanceId;
                });

                if (instanceId) {
                    return;
                }

                this.activePres.props.SurveyInstances.push(survey.instanceId);
                this.surveys.push(survey);
            });

            this.service.addEventListener('surveyStopped', (survey) => {
                if (!this.activePres  || !this.activePres.props || !this.activePres.props.SurveyInstances) {
                    return;
                }

                for(let i = 0; i < this.activePres.props.SurveyInstances.length; ++i) {
                    if (this.activePres.props.SurveyInstances[i] === survey.instanceId) {
                        this.activePres.props.SurveyInstances.splice(i, 1);
                    }
                }

                for(let i = 0; i < this.surveys.length; ++i) {
                    if (this.surveys[i].instanceId === survey.instanceId) {
                        this.surveys.splice(i, 1);
                    }
                }
            });
        },

        resetSurveys() {
            if (!this.activePres || !this.activePres.props) {
                return;
            }

            this.surveys = [];

            let surveys = this.activePres.props.Surveys;

            if (surveys) { // Old api
                this.surveys = surveys;
            } else { // New api
                let surveyInstances = this.activePres.props.SurveyInstances;

                if (!surveyInstances) {
                    return;
                }

                surveyInstances.map(async (surveyInstanceId) => {
                    this.surveys.push(await this.service.getSurvey(this.activePres.id, surveyInstanceId));
                });
            }
        },

        getSlideIndexFromId(id) {
            for(let i = 0; i < this.sortedSlides.length; ++i) {
                if (this.sortedSlides[i].id === id) {
                    return i;
                }
            }

            return 0;
        },

        async getPresentations(handle, callback) {
            if (!this.isReady) {
                return;
            }

            let presentations;

            if (handle) {
                presentations = await this.service.getPresentations(handle);
            } else {
                presentations = [];
                console.log('Undefined handle');
            }

            if (callback) {
                callback(presentations);
            }

            return presentations;
        },

        async openPresentation(presentation) {
            if (!presentation) {
                return;
            }

            this.activePres = presentation;
            this.slideList = await this.service.getSlides(presentation.id);
            this.revealOn = false;
            this.service.joinPresentation(this.activePres, this.user).then(() => {
                this.addPresentationListeners();
            });
            this.peopleList = undefined;
            this.questionList = undefined;
            this.resetSurveys();

            if (presentation.pinned) {
                this.progressiveReveal = this.slideList.length;
            } else {
                this.progressiveReveal = this.getSlideIndexFromId(presentation.currentSlide);
            }
        },

        onNotificationClicked() {
            if (!this.connectionParams) {
                return;
            }
            this.notificationForAll = undefined;

            let params = `dialogloopkey=${this.connectionParams.dialogloopkey}&token=${this.connectionParams.token}`;

            if (this.connectionParams.session) {
                params += `&session=${this.connectionParams.session}`;
            } else if (this.connectionParams.presentation) {
                params += `&presentation=${this.connectionParams.presentation}`;
            }

            if (NavigatorUtils.isAndroid()) {
                let ifr = document.createElement('iframe');
               
                ifr.src = androidIntent;
                ifr.style.display = 'none';
                document.body.appendChild(ifr);

                setTimeout(() => {
                    document.body.removeChild(ifr);
                }, 5000);
            } else if (NavigatorUtils.isIOS()) {
                let saveHref = document.location.href
                document.location.href = `${iOSIntent}?${params}`;

                setTimeout(() => {
                    document.location.href = saveHref;
                }, 400);
            } else {
                return;
            }
        }
    },

    async mounted() {
        EventBus.$on('getUser', async (id) => {
            this.user = await this.service.getUser(id);
        });

        EventBus.$on('logout', () => {
            this.user = undefined;
            this.activePres = undefined;
            this.$router.replace({
                path: 'login',
                query: this.$route.query
            });
        });

        EventBus.$on('getPresentations', async (handle) => {   
            this.presentationList = this.getPresentations(handle);
        });

        EventBus.$on('getPeople', async (id) => {
            if (this.peopleList !== undefined) {
                return;
            }
            this.peopleList = await this.service.getPeople(id);

            this.peopleMap = new Map();
            this.peopleList.forEach(async (person) => {             
                this.peopleMap.set(person.id, person);
            });
        });

        EventBus.$on('postQuestion', async (question) => {
            if (question && this.user) {
                this.service.postQuestion(this.activePres.id, this.user.id, question);
            }
        });

        EventBus.$on('postMessage', async (message, contactId) => {
            if (message && this.user && this.activePres) {
                this.service.postMessage(this.activePres.id, this.user.id, contactId, message);
            }
        }); 

        EventBus.$on('unlinkAccount', async (socialMedia) => {
            this.service.unlinkAccount(this.user.id, socialMedia);
        });

        EventBus.$on('likeQuestion', async (question) => {
            if (question) {
                this.service.likeQuestion(this.activePres.id, this.user.id, question.id);
            }
        });

        EventBus.$on('likeSlide', async (slide) => {
            if (slide) {
                this.service.likeSlide(this.activePres.id, slide.id);
            }
        });

        EventBus.$on('getSlides', async (id) => {
            this.slideList = await this.service.getSlides(id);
            this.revealOn = false;
            this.syncSlideNum = this.getSlideIndexFromId(this.activePres.currentSlide);

            if (this.activePres.pinned) {
                this.progressiveReveal = this.slideList.length;
            } else {
                this.progressiveReveal = this.syncSlideNum; // Issue if the presenter came back to an early slide before the attendee joins. The attendee won't be able to access all the slides that were shown before. Wait for an update on the props from PPT to check the latest shown slide
            }
        });

        EventBus.$on('openPresentation', (presentation) => {
            this.openPresentation(presentation);
        });

        EventBus.$on('getQuestions', async (id) => {
            if (this.questionList !== undefined) {
                return;
            }

            this.questionList = await this.service.getQuestions(id);
            this.questionList.sort((a, b) => a.timestamp - b.timestamp);
        });

        EventBus.$on('postQuestionProps', async (reply) => {
            if (!reply || !this.user || !this.activePres) {
                return;
            }

            this.service.replyQuestion(this.activePres.id, this.user.id, reply.questionId, reply.comment);
        });
        
        EventBus.$on('getMessages', async (contactId) => {
            this.chatList = await this.service.getMessages(this.user.id, contactId);
            this.chatList.sort((a, b) => a.timestamp - b.timestamp);
        });

        EventBus.$on('getContacts', async (id) => {
            if (!this.user) {
                return;
            }

            let chatContacts = await this.service.getContacts(this.user.id);
            let i = 1;

            chatContacts = chatContacts.sort((contact1, contact2) => contact1.to[0] < contact2.to[0]);

            while(i < chatContacts.length) {
                if (chatContacts[i - 1].to[0] === chatContacts[i].to[0]) {
                    chatContacts.splice(i, 1);
                } else {
                    ++i;
                }
            }

            this.chatContacts = chatContacts.sort((contact1, contact2) => contact1.timestamp < contact2.timestamp);
        });

        EventBus.$on('submitSurvey', async (json) => {
            if (this.activePres) {
                this.service.submitSurvey(this.activePres.id, json);
            }
        });

        EventBus.$on('reloadWithShortCodeUrl', (shortCodeUrl) => {
            if (!shortCodeUrl) {
                return;
            }

            try {
                let query = JSON.parse(JSON.stringify(this.$route.query));

                query.shortCode = shortCodeUrl;
                this.$router.push({
                    path: '/',
                    query: query
                });
                this.$route.query.shortCode = encodeURIComponent(shortCodeUrl);
            } catch(e) {
                console.log(e);     
            }
        });

        EventBus.$on('tweetSlide', (tweet, callback) => {
            this.service.tweetSlide(tweet, this.user);

            if (callback) {
                callback();
            }
        });
    }
};
</script>

<style lang="scss">

.WebApp {
    overflow: hidden;
    background-color: #000;

    .bar-container {
        position: relative;
    }
}

</style>



// WEBPACK FOOTER //
// WebApp.vue?102e0e4b